import React, { useMemo } from "react";
import _ from "lodash/fp";
import { Alert } from "@actinc/dls/components";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormEditor,
  ControlledFormSelect,
} from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";
import { onDragEnd } from "../../../../services/utils";

const OrderInteractionForm = ({
  imageAssets = [],
  index,
  interaction,
  readonly,
}) => {
  const renderDraggable = useDraggableInPortal();

  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "OrderInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const { control, getValues, trigger, watch } = useFormContext();
  const [orientation] = watch(
    `${namePrefix}.orientation`,
    `${namePrefix}.correctResponse`,
    `${namePrefix}.elements`,
  );

  const {
    append: appendElement,
    fields: elements,
    move: moveElement,
    remove: removeElement,
  } = useFieldArray({
    control,
    name: `${namePrefix}.elements`,
  });
  const {
    append: appendCorrectResponse,
    fields: correctResponses,
    move: moveCorrectResponse,
    remove: removeCorrectResponse,
  } = useFieldArray({
    control,
    name: `${namePrefix}.correctResponse`,
  });

  const correctOrderWarning = useMemo(() => {
    if (elements.length <= 2)
      return "Warning: The interaction has only two elements";
    if (
      elements.every(
        (el, idx) => el?.identifier === correctResponses[idx]?.identifier,
      )
    )
      return "Warning: The Element order for the Correct Response matches the created order";
    return "";
  }, [correctResponses, elements]);

  const addElement = () => {
    const highestElementNumber = Math.max(
      ...elements.map((el) => {
        return el?.identifier.slice(7);
      }),
    );
    appendElement({
      content: "<p></p>",
      identifier: `Element${highestElementNumber + 1}`,
    });
    appendCorrectResponse({
      identifier: `Element${highestElementNumber + 1}`,
    });
  };
  const deleteElement = (i) => () => {
    removeElement(i);
    const correctResponseIndex = correctResponses.findIndex(
      (cr) => cr?.identifier === elements[i]?.identifier,
    );
    removeCorrectResponse(correctResponseIndex);
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <>
      <Box
        sx={{
          alignItems: "self-start",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <ControlledFormSelect
          control={control}
          defaultValue={interaction.orientation}
          fullWidth={true}
          id={`${namePrefix}.orientation`}
          label="Orientation"
          name={`${namePrefix}.orientation`}
          options={["vertical", "horizontal"].map((value) => ({
            label: _.upperFirst(value),
            value,
          }))}
          readonly={readonly}
          rules={{
            required: true,
          }}
          trigger={() => trigger([`${namePrefix}.orientation`])}
        />

        <DragDropContext onDragEnd={onDragEnd(moveElement)}>
          <Droppable droppableId={`${interaction.identifier}-elements`}>
            {(droppableProvided) => {
              return (
                <List
                  dense={true}
                  disablePadding={true}
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {!!elements &&
                    elements.map((element, i) => {
                      return (
                        <Draggable
                          draggableId={element.id}
                          index={i}
                          isDragDisabled={readonly}
                          key={element.id}
                        >
                          {renderDraggable((draggableProvided) => (
                            <ListItem
                              dense={true}
                              disableGutters={true}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                            >
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                    }}
                                  >
                                    <Box {...draggableProvided.dragHandleProps}>
                                      <DragHorizontalVariantIcon />
                                    </Box>
                                    <Typography variant="subtitle1">{`Element ${
                                      i + 1
                                    }`}</Typography>
                                  </Box>
                                  {!readonly && (
                                    <Tooltip title="Delete Element">
                                      <IconButton
                                        aria-label={`delete element ${i + 1}`}
                                        disabled={elements.length <= 2}
                                        onClick={deleteElement(i)}
                                        size="large"
                                        sx={{ padding: 1 }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <ControlledFormEditor
                                  assets={{ images: imageAssets }}
                                  control={control}
                                  id={`${namePrefix}.elements[${i}].content`}
                                  name={`${namePrefix}.elements[${i}].content`}
                                  onReady={(ed) => {
                                    console.info(
                                      `Item Interaction ${namePrefix} element ${i} content editor is ready, %o`,
                                      ed,
                                    );
                                  }}
                                  options={["blockOrder"]}
                                  readonly={readonly}
                                  type="item"
                                />
                              </Box>
                            </ListItem>
                          ))}
                        </Draggable>
                      );
                    })}
                </List>
              );
            }}
          </Droppable>
        </DragDropContext>
        {!readonly && (
          <Button
            onClick={addElement}
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Create Element
          </Button>
        )}
        <Box aria-label="correct-order-response">
          <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
            Correct Order
          </Typography>
          <DragDropContext onDragEnd={onDragEnd(moveCorrectResponse)}>
            <Droppable
              aria-label="correct-response-order"
              droppableId={`${interaction.correctResponse}-elements`}
              direction={
                orientation === "horizontal" ? "horizontal" : "vertical"
              }
            >
              {(droppableProvided) => {
                return (
                  <Box>
                    <List
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      dense={true}
                      disablePadding={true}
                      sx={{
                        flexDirection: () =>
                          orientation === "horizontal" ? "row" : "column",
                      }}
                    >
                      {!!correctResponses &&
                        correctResponses.map((cr, i) => {
                          const displayText = elements.find(
                            (c) => c?.identifier === cr?.identifier,
                          );
                          return (
                            <Draggable
                              draggableId={cr.identifier}
                              index={i}
                              isDragDisabled={readonly}
                              key={cr.identifier}
                            >
                              {renderDraggable((draggableProvided) => (
                                <Box
                                  ref={draggableProvided.innerRef}
                                  sx={{
                                    backgroundColor: "#f3f3f3",
                                    borderRadius: "5px",
                                    minWidth: 100,
                                    margin: 2,
                                    maxWidth: "500px",
                                    padding: 3,
                                    textAlign: "center",
                                  }}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                >
                                  <ListItem dense={true} disableGutters={true}>
                                    {
                                      <>
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: displayText?.content,
                                          }}
                                          id="correct-response"
                                        />
                                      </>
                                    }
                                  </ListItem>
                                </Box>
                              ))}
                            </Draggable>
                          );
                        })}
                    </List>
                  </Box>
                );
              }}
            </Droppable>
          </DragDropContext>
          {correctOrderWarning && (
            <Alert
              severity="warning"
              sx={{ padding: 1 }}
              title={correctOrderWarning}
            ></Alert>
          )}
        </Box>
      </Box>
    </>
  );
};

export { OrderInteractionForm };
